import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi, setToken } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import Loading from "@/components/app/Loading";
import { toast } from "sonner";

/**
 * Landing page for the passwordless sign-in email.
 * Reads ?token=... from the URL, POSTs it to /api/auth/email/verify,
 * stores the returned session_token, and forwards to the dashboard.
 * Mirrors Callback.js semantics so both auth paths end at the same state.
 */
export default function MagicLinkVerify() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [status, setStatus] = useState("Signing you in...");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    (async () => {
      const qs = new URLSearchParams(window.location.search);
      const token = qs.get("token");
      if (!token) {
        setError("This sign-in link is missing its token. Please request a new one.");
        setTimeout(() => navigate("/login", { replace: true }), 2000);
        return;
      }

      try {
        const res = await authApi.magicLinkVerify(token);
        if (res.success && res.session_token) {
          setToken(res.session_token);
          const granted = res.auto_granted_products || [];
          if (granted.length) {
            const label = granted.length === 1
              ? "Your purchase is now in your Library"
              : `${granted.length} purchases were added to your Library`;
            toast.success(label);
          }
          setStatus("Signed in. Redirecting...");
          await refresh();
          navigate("/dashboard", { replace: true });
        } else {
          throw new Error("Sign-in failed. Please request a new link.");
        }
      } catch (e) {
        setError(e.message || "Sign-in failed");
        setTimeout(() => navigate("/login", { replace: true }), 2500);
      }
    })();
  }, [navigate, refresh]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center gap-4"
      style={{ background: "var(--pa-background)" }}
      data-testid="magic-link-verify-page"
    >
      {error ? (
        <div className="text-center px-6 max-w-md">
          <p className="pa-serif text-xl mb-2" style={{ color: "var(--pa-error)" }}>
            Sign-in failed
          </p>
          <p className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>{error}</p>
          <p className="text-xs mt-3" style={{ color: "var(--pa-text-muted)" }}>
            Redirecting you to the sign-in page...
          </p>
        </div>
      ) : (
        <Loading label={status} />
      )}
    </div>
  );
}
